/**
 * Response Projection Transformation Utility
 *
 * Transforms API responses into ActionCardData based on response_projection configuration
 * Uses JSONPath to extract field values according to projection.fields
 */

import { JSONPath } from 'jsonpath-plus';
import { ResponseProjection, ProjectionField } from '@/types/mcpTool';
import { ActionCardData, ActionCardDataItem } from '@/types/action-card';

/**
 * Extract a single value from response using JSONPath
 */
function extractValue(response: any, path: string): any {
  try {
    const result = JSONPath({ path, json: response, wrap: false });
    return result;
  } catch (error) {
    console.warn(`[ResponseProjection] Failed to extract path "${path}":`, error);
    return null;
  }
}

/**
 * Extract all fields from a single item using projection fields
 */
function extractFields(item: any, fields: ProjectionField[]): Record<string, any> {
  const extracted: Record<string, any> = {};

  for (const field of fields) {
    const value = extractValue(item, field.path);
    if (value !== null && value !== undefined) {
      extracted[field.label] = value;
    }
  }

  return extracted;
}

/**
 * Transform backend's formatted_response to ActionCardData
 * Backend already extracts and formats data, we just need to map it to ActionCardData
 *
 * @param formattedResponse - Backend's formatted response with mode and items
 * @param toolName - Name of the tool
 * @param actionName - Name of the action
 * @returns ActionCardData for rendering
 */
export function transformFormattedResponse(
  formattedResponse: any,
  toolName: string,
  actionName: string
): ActionCardData {
  const timestamp = new Date().toISOString();
  const cardId = `tool-result-${Date.now()}`;

  console.log('🔄 [ResponseProjection] Using backend formatted_response:', {
    mode: formattedResponse.mode,
    items_count: formattedResponse.items?.length
  });

  if (formattedResponse.mode === 'list') {
    // Transform formatted items to ActionCardDataItems
    const cardItems: ActionCardDataItem[] = (formattedResponse.items || []).map((item: any, index: number) => {
      const values = item.values || [];

      // Use first value as title, second as description
      const title = values[0]?.value ? String(values[0].value) : `Item ${index + 1}`;
      const description = values[1]?.value ? String(values[1].value) : undefined;

      // Remaining values go into meta
      const meta: Record<string, any> = {};
      values.forEach((v: any, idx: number) => {
        if (idx >= 2 && v.value !== null && v.value !== undefined) {
          meta[v.label] = v.value;
        }
      });

      return {
        id: `${cardId}-item-${index}`,
        title,
        description,
        meta: Object.keys(meta).length > 0 ? meta : undefined,
      };
    });

    console.log(`✅ [ResponseProjection] Formatted ${cardItems.length} items for list card`);

    return {
      id: cardId,
      title: `${toolName}: ${actionName}`,
      summary: `Found ${cardItems.length} result${cardItems.length !== 1 ? 's' : ''}`,
      status: 'success',
      timestamp,
      type: 'list',
      items: cardItems,
      isCollapsible: cardItems.length > 5,
    };
  } else {
    // Singular mode - extract values to payload
    const values = formattedResponse.items?.[0]?.values || [];
    const payload: Record<string, any> = {};

    values.forEach((v: any) => {
      if (v.value !== null && v.value !== undefined) {
        payload[v.label] = v.value;
      }
    });

    console.log('✅ [ResponseProjection] Formatted singular card with payload:', payload);

    return {
      id: cardId,
      title: `${toolName}: ${actionName}`,
      summary: Object.entries(payload)
        .slice(0, 2)
        .map(([label, value]) => `${label}: ${value}`)
        .join(', '),
      status: 'success',
      timestamp,
      type: 'api',
      payload,
    };
  }
}

/**
 * Transform API response to ActionCardData using response_projection
 *
 * @param response - Raw API response
 * @param projection - Response projection configuration
 * @param toolName - Name of the tool that generated this response
 * @param actionName - Name of the action that was executed
 * @returns ActionCardData for rendering in conversation
 */
export function transformToolResultToActionCard(
  response: any,
  projection: ResponseProjection,
  toolName: string,
  actionName: string
): ActionCardData {
  // Check if backend already formatted the response
  if (response.formatted_response) {
    console.log('🎯 [ResponseProjection] Using backend formatted_response');
    return transformFormattedResponse(response.formatted_response, toolName, actionName);
  }

  // Fallback to JSONPath extraction
  const timestamp = new Date().toISOString();
  const cardId = `tool-result-${Date.now()}`;

  console.log('🔄 [ResponseProjection] Transforming response with JSONPath:', {
    mode: projection.mode,
    fields: projection.fields,
    items_path: projection.items_path,
    response_preview: JSON.stringify(response).substring(0, 200)
  });

  if (projection.mode === 'list') {
    // Extract items array using items_path
    const items = projection.items_path
      ? extractValue(response, projection.items_path)
      : response;

    if (!Array.isArray(items)) {
      console.error('[ResponseProjection] items_path did not return array:', items);
      return {
        id: cardId,
        title: `${toolName}: ${actionName}`,
        summary: 'Error: Expected array from items_path',
        status: 'error',
        timestamp,
        type: 'api',
      };
    }

    // Transform each item to ActionCardDataItem
    const cardItems: ActionCardDataItem[] = items.map((item, index) => {
      const fields = extractFields(item, projection.fields);

      // Use first field as title, second as description
      const fieldValues = Object.values(fields);
      const title = fieldValues[0] ? String(fieldValues[0]) : `Item ${index + 1}`;
      const description = fieldValues[1] ? String(fieldValues[1]) : undefined;

      // Remaining fields go into meta
      const meta: Record<string, any> = {};
      Object.entries(fields).forEach(([label, value], idx) => {
        if (idx >= 2) { // Skip first two (title, description)
          meta[label] = value;
        }
      });

      return {
        id: `${cardId}-item-${index}`,
        title,
        description,
        meta: Object.keys(meta).length > 0 ? meta : undefined,
      };
    });

    console.log(`✅ [ResponseProjection] Extracted ${cardItems.length} items for list card`);

    return {
      id: cardId,
      title: `${toolName}: ${actionName}`,
      summary: `Found ${cardItems.length} result${cardItems.length !== 1 ? 's' : ''}`,
      status: 'success',
      timestamp,
      type: 'list',
      items: cardItems,
      isCollapsible: cardItems.length > 5,
    };

  } else {
    // Singular mode - single object
    const fields = extractFields(response, projection.fields);

    console.log('✅ [ResponseProjection] Extracted fields for singular card:', fields);

    return {
      id: cardId,
      title: `${toolName}: ${actionName}`,
      summary: Object.entries(fields)
        .slice(0, 2)
        .map(([label, value]) => `${label}: ${value}`)
        .join(', '),
      status: 'success',
      timestamp,
      type: 'api',
      payload: fields,
    };
  }
}

/**
 * Create an error card when transformation fails
 */
export function createErrorActionCard(
  toolName: string,
  actionName: string,
  errorMessage: string
): ActionCardData {
  return {
    id: `tool-error-${Date.now()}`,
    title: `${toolName}: ${actionName}`,
    summary: 'Execution failed',
    status: 'error',
    timestamp: new Date().toISOString(),
    type: 'api',
    payload: {
      error: errorMessage,
    },
  };
}

/**
 * Create a pending card while tool is executing
 */
export function createPendingActionCard(
  toolName: string,
  actionName: string
): ActionCardData {
  return {
    id: `tool-pending-${Date.now()}`,
    title: `${toolName}: ${actionName}`,
    summary: 'Executing...',
    status: 'pending',
    timestamp: new Date().toISOString(),
    type: 'api',
  };
}
